import { useCallback, useEffect, useRef } from 'react';

import getCookieValue from '~/utils/getCookieValue';
import { hashSha256 } from '~/utils/sha256';
import { generateUUID } from '~/utils/uuid';

import { VISIT_TOKEN_READY_EVENT_NAME } from './useLoaders/useCreateVisitToken';
import { useNonRevalidatingFetcher } from './useNonRevalidatingFetcher';

const buildEmailHashSha256 = (email?: string): string | undefined => {
  if (!email) return undefined;
  try {
    return hashSha256(email.toLowerCase());
  } catch {
    return undefined;
  }
};

// Sanitizes a URL by replacing jwt_token query parameter with a placeholder
const sanitizeUrl = (url: string): string => {
  try {
    const urlObj = new URL(url);
    if (urlObj.searchParams.has('jwt_token')) {
      urlObj.searchParams.set('jwt_token', 'REDACTED');
    }
    return urlObj.toString();
  } catch {
    return url;
  }
};

// In-memory storage for anonymous user ID when localStorage is unavailable
let inMemoryAnonymousId: string | null = null;

const getOrCreateAnonymousUserId = (): string => {
  const STORAGE_KEY = 'beehiiv_anonymous_user_id';

  try {
    let anonymousId = localStorage.getItem(STORAGE_KEY);

    if (!anonymousId) {
      anonymousId = generateUUID();
      localStorage.setItem(STORAGE_KEY, anonymousId);
    }

    return anonymousId;
  } catch {
    if (!inMemoryAnonymousId) {
      inMemoryAnonymousId = generateUUID();
    }
    return inMemoryAnonymousId;
  }
};

export { getOrCreateAnonymousUserId };

const getNectarVisitToken = (): string => {
  if (typeof document === 'undefined') return '';
  return getCookieValue('visit_token', document.cookie) || '';
};

export { getNectarVisitToken };

// Enum for analytics event types
export enum AnalyticsEventType {
  PAGEVIEW = 'pageview',
  CLICK = 'click',
}

// Types for analytics events
interface AnalyticsEvent {
  user_agent: string;
  url: string;
  event: AnalyticsEventType;
  client_timestamp: number;
  event_id: string;
  nectar_id: string;
  publication_id: string;
  visit_token: string;
  ip: string;
  store_ip: boolean;
  url_referrer?: string;
  click_type?: string;
  click_url?: string;
  click_raw_tag_name?: string;
  sendable_id?: string;
  sendable_type?: string;
  subscription_id?: number;
  email_hash_sha256?: string;
  anonymous_user_id: string;
}

export interface NectarAnalyticsSettings {
  batchSize: number;
  batchTimeoutMs: number;
  pageViewTracking: boolean;
  clickTracking: boolean;
  ipTracking: boolean;
}

interface UseNectarAnalyticsProps {
  nectarId: string;
  enabled: boolean;
  publicationId: string;
  postId?: string;
  subscriptionId?: number;
  email?: string;
  settings: NectarAnalyticsSettings;
}

interface UseNectarAnalyticsReturn {
  trackPageView: () => void;
  trackClick: (clickData: { clickType: string; clickUrl?: string; clickRawTagName?: string }) => void;
}

// Hook for tracking analytics events
export const useNectarAnalytics = ({
  nectarId,
  enabled,
  publicationId,
  postId,
  subscriptionId,
  email,
  settings,
}: UseNectarAnalyticsProps): UseNectarAnalyticsReturn => {
  const lastPageViewRef = useRef<string>('');
  const eventQueue = useRef<AnalyticsEvent[]>([]);
  const visitTokenPendingEventsRef = useRef<AnalyticsEvent[]>([]);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isProcessingRef = useRef<boolean>(false);
  const fetcher = useNonRevalidatingFetcher();

  // Extract visit token from existing visit_token cookie
  const extractVisitToken = useCallback((): string | null => {
    const token = getNectarVisitToken();
    return token || null;
  }, []);

  // Send batch to server
  const sendBatch = useCallback(
    (events: AnalyticsEvent[]): void => {
      fetcher.submit(
        JSON.stringify({
          events,
          settings,
        }),
        {
          method: 'post',
          action: '/nectar_analytics',
          encType: 'application/json',
        },
      );
    },
    [fetcher, settings],
  );

  // Process the event queue
  const processQueue = useCallback(() => {
    if (isProcessingRef.current || eventQueue.current.length === 0) {
      return;
    }

    isProcessingRef.current = true;

    // Take up to batchSize events from the queue
    const eventsToProcess = eventQueue.current.splice(0, settings.batchSize);

    if (eventsToProcess.length === 0) {
      isProcessingRef.current = false;
      return;
    }

    sendBatch(eventsToProcess);

    isProcessingRef.current = false;

    // Schedule next processing if there are more events
    if (eventQueue.current.length > 0) {
      // Schedule processing after timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        processQueue();
      }, settings.batchTimeoutMs);
    }
  }, [settings.batchSize, settings.batchTimeoutMs, sendBatch]);

  // Schedule queue processing
  const scheduleProcessing = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      processQueue();
    }, settings.batchTimeoutMs);
  }, [processQueue, settings.batchTimeoutMs]);

  // Force flush the queue (useful for page unload, etc.)
  const flushQueue = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    processQueue();
  }, [processQueue]);

  // Send event to analytics endpoint
  const sendEvent = useCallback(
    (event: AnalyticsEvent): void => {
      eventQueue.current.push(event);

      // If we've reached the batch size, process immediately
      if (eventQueue.current.length >= settings.batchSize) {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        processQueue();
      } else {
        // Otherwise, schedule processing after timeout
        scheduleProcessing();
      }
    },
    [processQueue, scheduleProcessing, settings.batchSize],
  );

  // Flush events that were pending for visit token
  const flushEventsPendingForVisitToken = useCallback((): void => {
    if (visitTokenPendingEventsRef.current.length === 0) {
      return;
    }

    const visitToken = extractVisitToken();
    if (!visitToken) {
      return;
    }

    // Process all events now that we have a visit token
    const pendingEvents = visitTokenPendingEventsRef.current.splice(0);
    pendingEvents.forEach((event) => {
      // Fill in the visit token for events that were created without it
      event.visit_token = visitToken;
      sendEvent(event as AnalyticsEvent);
    });
  }, [extractVisitToken, sendEvent]);

  // Create full event with current data (visit_token may be missing)
  const createEvent = useCallback(
    (params: {
      event_type: AnalyticsEventType;
      click_type?: string;
      click_url?: string;
      click_raw_tag_name?: string;
    }): AnalyticsEvent => {
      return {
        user_agent: navigator.userAgent,
        url: sanitizeUrl(window.location.href),
        event: params.event_type,
        client_timestamp: Date.now(),
        event_id: generateUUID(),
        nectar_id: nectarId,
        publication_id: publicationId,
        visit_token: extractVisitToken() || '', // Will be empty if not available
        ip: '', // Will be populated by server-side action
        store_ip: settings.ipTracking,
        url_referrer: document.referrer || undefined,
        sendable_id: postId,
        sendable_type: postId ? 'post' : undefined,
        subscription_id: subscriptionId,
        email_hash_sha256: buildEmailHashSha256(email),
        anonymous_user_id: getOrCreateAnonymousUserId(),
        click_type: params.click_type,
        click_url: params.click_url,
        click_raw_tag_name: params.click_raw_tag_name,
      };
    },
    [extractVisitToken, nectarId, publicationId, settings.ipTracking, postId, subscriptionId, email],
  );

  // Queue event pending for visit token
  const queueEventPendingForVisitToken = useCallback((event: AnalyticsEvent): void => {
    visitTokenPendingEventsRef.current.push(event);
  }, []);

  // Track page view
  const trackPageView = useCallback((): void => {
    if (!settings.pageViewTracking) return;

    const currentUrl = window.location.href;

    // Don't track duplicate page views for the same URL
    if (currentUrl === lastPageViewRef.current) return;
    lastPageViewRef.current = currentUrl;

    const event = createEvent({ event_type: AnalyticsEventType.PAGEVIEW });

    // If no visit token, queue it; otherwise send immediately
    if (!event.visit_token) {
      queueEventPendingForVisitToken(event);
    } else {
      sendEvent(event);
    }
  }, [createEvent, sendEvent, settings.pageViewTracking, queueEventPendingForVisitToken]);

  // Track click event
  const trackClick = useCallback(
    ({
      clickType,
      clickUrl,
      clickRawTagName,
    }: {
      clickType: string;
      clickUrl?: string;
      clickRawTagName?: string;
    }): void => {
      if (!settings.clickTracking) return;

      const event = createEvent({
        event_type: AnalyticsEventType.CLICK,
        click_type: clickType,
        click_url: clickUrl,
        click_raw_tag_name: clickRawTagName,
      });

      // If no visit token, queue it; otherwise send immediately
      if (!event.visit_token) {
        queueEventPendingForVisitToken(event);
      } else {
        sendEvent(event);
      }
    },
    [createEvent, sendEvent, settings.clickTracking, queueEventPendingForVisitToken],
  );

  // Listen for visitTokenReady event
  useEffect(() => {
    const handleVisitTokenReady = () => {
      flushEventsPendingForVisitToken();
    };

    window.addEventListener(VISIT_TOKEN_READY_EVENT_NAME, handleVisitTokenReady);

    return () => {
      window.removeEventListener(VISIT_TOKEN_READY_EVENT_NAME, handleVisitTokenReady);
    };
  }, [flushEventsPendingForVisitToken]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Flush queue on page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      flushQueue();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [flushQueue]);

  // Early return with no-op methods if analytics is disabled
  if (!enabled) {
    return {
      trackPageView: () => {},
      trackClick: () => {},
    };
  }

  return {
    trackPageView,
    trackClick,
  };
};

export type { AnalyticsEvent, UseNectarAnalyticsProps, UseNectarAnalyticsReturn };
